import { BRAND_TO_INN, CN_TO_INN, INN_TO_CN } from "./inn_data";
import {
  EXTENDED_BRAND_TO_INN,
  EXTENDED_CN_TO_INN,
  EXTENDED_INN_TO_CN,
} from "./inn_extended_data";

export interface DrugMapping {
  id: string;
  nameZh: string;
  nameEn: string;
  inn?: string;
  aliasesZh: string[];
  aliasesEn: string[];
  pinyin: string[];
  pinyinAbbr: string;
  category: string;
  therapeuticClass: string;
}

const THERAPEUTIC_HINTS: Array<{
  keywords: string[];
  category: string;
  therapeuticClass: string;
}> = [
  { keywords: ["gliflozin"], category: "降糖药", therapeuticClass: "SGLT-2抑制剂" },
  { keywords: ["gliptin"], category: "降糖药", therapeuticClass: "DPP-4抑制剂" },
  { keywords: ["glutide", "exenatide"], category: "降糖药", therapeuticClass: "GLP-1受体激动剂" },
  { keywords: ["statin"], category: "调脂药", therapeuticClass: "HMG-CoA还原酶抑制剂" },
  { keywords: ["prazole"], category: "消化系统用药", therapeuticClass: "质子泵抑制剂" },
  { keywords: ["sartan"], category: "心血管用药", therapeuticClass: "ARB类降压药" },
  { keywords: ["pril"], category: "心血管用药", therapeuticClass: "ACE抑制剂" },
  { keywords: ["oxaban", "gatran"], category: "抗凝血药", therapeuticClass: "新型口服抗凝药" },
  { keywords: ["cillin", "cef", "cycline", "mycin"], category: "抗感染药", therapeuticClass: "抗菌药物" },
  { keywords: ["nifedipine", "amlodipine", "dipine"], category: "心血管用药", therapeuticClass: "钙通道阻滞剂" },
];

function inferClass(inn: string) {
  const lower = inn.toLowerCase();
  const hit = THERAPEUTIC_HINTS.find(item => item.keywords.some(k => lower.includes(k)));
  return hit || { category: "常用药物", therapeuticClass: "未分类" };
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

const ALL_INN_TO_CN: Record<string, string> = {
  ...EXTENDED_INN_TO_CN,
  ...INN_TO_CN,
};

const ALL_CN_TO_INN: Record<string, string> = {
  ...EXTENDED_CN_TO_INN,
  ...CN_TO_INN,
};

const ALL_BRAND_TO_INN: Record<string, string> = {
  ...EXTENDED_BRAND_TO_INN,
  ...BRAND_TO_INN,
};

const DOSAGE_FORM_SUFFIXES = [
  "薄膜衣片",
  "缓释胶囊",
  "控释胶囊",
  "肠溶胶囊",
  "缓释片",
  "控释片",
  "肠溶片",
  "分散片",
  "口崩片",
  "咀嚼片",
  "泡腾片",
  "胶囊剂",
  "颗粒剂",
  "干混悬剂",
  "口服溶液",
  "口服液",
  "注射液",
  "注射剂",
  "滴眼液",
  "胶囊",
  "颗粒",
  "片剂",
  "片",
].sort((a, b) => b.length - a.length);

const SALT_PREFIXES = [
  "盐酸",
  "氢溴酸",
  "氢碘酸",
  "枸橼酸",
  "柠檬酸",
  "马来酸",
  "富马酸",
  "琥珀酸",
  "酒石酸",
  "草酸",
  "苹果酸",
  "苯磺酸",
  "甲磺酸",
  "磷酸",
  "硫酸",
  "硝酸",
  "醋酸",
  "乳酸",
];

const SALT_SUFFIXES = [
  "盐酸盐",
  "钠盐",
  "钾盐",
  "钙盐",
  "镁盐",
  "钠",
  "钾",
  "钙",
  "镁",
];

const EXTRA_DRUG_MAPPINGS: DrugMapping[] = [
  {
    id: "extra_buspirone",
    nameZh: "丁螺环酮",
    nameEn: "buspirone",
    inn: "buspirone",
    aliasesZh: ["丁螺环酮", "盐酸丁螺环酮", "盐酸丁螺环酮片"],
    aliasesEn: ["buspirone", "buspirone hydrochloride", "buspar"],
    pinyin: [],
    pinyinAbbr: "",
    category: "精神神经用药",
    therapeuticClass: "抗焦虑药",
  },
  {
    id: "extra_xanomeline_trospium_chloride",
    nameZh: "呫诺美林曲司氯铵",
    nameEn: "xanomeline_trospium_chloride",
    inn: "xanomeline_trospium_chloride",
    aliasesZh: ["呫诺美林曲司氯铵", "咕诺美林曲司氯铵", "呫诺美林/曲司氯铵", "咕诺美林/曲司氯铵"],
    aliasesEn: ["xanomeline trospium chloride", "xanomeline and trospium chloride", "xanomeline_and_trospium_chloride", "cobenfy", "karxt"],
    pinyin: [],
    pinyinAbbr: "",
    category: "精神神经用药",
    therapeuticClass: "毒蕈碱受体激动剂/拮抗剂复方",
  },
  {
    id: "extra_ropinirole",
    nameZh: "罗匹尼罗",
    nameEn: "ropinirole",
    inn: "ropinirole",
    aliasesZh: ["罗匹尼罗", "盐酸罗匹尼罗", "盐酸罗匹尼罗片", "盐酸罗匹尼罗缓释片"],
    aliasesEn: ["ropinirole", "ropinirole hydrochloride", "requip", "requip xl"],
    pinyin: [],
    pinyinAbbr: "",
    category: "神经系统用药",
    therapeuticClass: "多巴胺受体激动剂",
  },
  {
    id: "extra_pramipexole",
    nameZh: "普拉克索",
    nameEn: "pramipexole",
    inn: "pramipexole",
    aliasesZh: ["普拉克索", "盐酸普拉克索", "盐酸普拉克索片"],
    aliasesEn: ["pramipexole", "pramipexole dihydrochloride", "mirapex", "mirapex er"],
    pinyin: [],
    pinyinAbbr: "",
    category: "神经系统用药",
    therapeuticClass: "多巴胺受体激动剂",
  },
  {
    id: "extra_rotigotine",
    nameZh: "罗替戈汀",
    nameEn: "rotigotine",
    inn: "rotigotine",
    aliasesZh: ["罗替戈汀", "罗替戈汀贴片"],
    aliasesEn: ["rotigotine", "neupro"],
    pinyin: [],
    pinyinAbbr: "",
    category: "神经系统用药",
    therapeuticClass: "多巴胺受体激动剂",
  },
  {
    id: "extra_rasagiline",
    nameZh: "雷沙吉兰",
    nameEn: "rasagiline",
    inn: "rasagiline",
    aliasesZh: ["雷沙吉兰", "甲磺酸雷沙吉兰", "甲磺酸雷沙吉兰片"],
    aliasesEn: ["rasagiline", "rasagiline mesylate", "azilect"],
    pinyin: [],
    pinyinAbbr: "",
    category: "神经系统用药",
    therapeuticClass: "MAO-B 抑制剂",
  },
  {
    id: "extra_entacapone",
    nameZh: "恩他卡朋",
    nameEn: "entacapone",
    inn: "entacapone",
    aliasesZh: ["恩他卡朋", "恩他卡朋片"],
    aliasesEn: ["entacapone", "comtan"],
    pinyin: [],
    pinyinAbbr: "",
    category: "神经系统用药",
    therapeuticClass: "COMT 抑制剂",
  },
  {
    id: "extra_safinamide",
    nameZh: "沙芬酰胺",
    nameEn: "safinamide",
    inn: "safinamide",
    aliasesZh: ["沙芬酰胺", "甲磺酸沙芬酰胺", "甲磺酸沙芬酰胺片"],
    aliasesEn: ["safinamide", "safinamide mesylate", "xadago"],
    pinyin: [],
    pinyinAbbr: "",
    category: "神经系统用药",
    therapeuticClass: "MAO-B 抑制剂",
  },
  {
    id: "extra_sacubitril_valsartan",
    nameZh: "沙库巴曲缬沙坦",
    nameEn: "sacubitril_valsartan",
    inn: "sacubitril_valsartan",
    aliasesZh: ["沙库巴曲缬沙坦", "沙库巴曲缬沙坦钠", "沙库巴曲缬沙坦钠片"],
    aliasesEn: ["sacubitril valsartan", "sacubitril/valsartan", "entresto"],
    pinyin: [],
    pinyinAbbr: "",
    category: "心血管用药",
    therapeuticClass: "ARNI复方制剂",
  },
  {
    id: "extra_sitagliptin_metformin",
    nameZh: "西格列汀二甲双胍",
    nameEn: "sitagliptin_metformin",
    inn: "sitagliptin_metformin",
    aliasesZh: ["西格列汀二甲双胍", "西格列汀二甲双胍片", "西格列汀二甲双胍缓释片"],
    aliasesEn: ["sitagliptin metformin", "sitagliptin/metformin", "janumet"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "DPP-4抑制剂/双胍复方",
  },
  {
    id: "extra_linagliptin_metformin",
    nameZh: "利格列汀二甲双胍",
    nameEn: "linagliptin_metformin",
    inn: "linagliptin_metformin",
    aliasesZh: ["利格列汀二甲双胍", "利格列汀二甲双胍片"],
    aliasesEn: ["linagliptin metformin", "linagliptin/metformin", "jentadueto"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "DPP-4抑制剂/双胍复方",
  },
  {
    id: "extra_saxagliptin_metformin",
    nameZh: "沙格列汀二甲双胍",
    nameEn: "saxagliptin_metformin",
    inn: "saxagliptin_metformin",
    aliasesZh: ["沙格列汀二甲双胍", "沙格列汀二甲双胍片", "沙格列汀二甲双胍缓释片"],
    aliasesEn: ["saxagliptin metformin", "saxagliptin/metformin", "kombiglyze"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "DPP-4抑制剂/双胍复方",
  },
  {
    id: "extra_vildagliptin_metformin",
    nameZh: "维格列汀二甲双胍",
    nameEn: "vildagliptin_metformin",
    inn: "vildagliptin_metformin",
    aliasesZh: ["维格列汀二甲双胍", "维格列汀二甲双胍片"],
    aliasesEn: ["vildagliptin metformin", "vildagliptin/metformin", "eucreas"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "DPP-4抑制剂/双胍复方",
  },
  {
    id: "extra_alogliptin_metformin",
    nameZh: "阿格列汀二甲双胍",
    nameEn: "alogliptin_metformin",
    inn: "alogliptin_metformin",
    aliasesZh: ["阿格列汀二甲双胍", "阿格列汀二甲双胍片"],
    aliasesEn: ["alogliptin metformin", "alogliptin/metformin", "kazano"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "DPP-4抑制剂/双胍复方",
  },
  {
    id: "extra_valsartan_amlodipine",
    nameZh: "缬沙坦氨氯地平",
    nameEn: "valsartan_amlodipine",
    inn: "valsartan_amlodipine",
    aliasesZh: ["缬沙坦氨氯地平", "缬沙坦氨氯地平片", "氨氯地平缬沙坦片"],
    aliasesEn: ["valsartan amlodipine", "amlodipine valsartan", "exforge"],
    pinyin: [],
    pinyinAbbr: "",
    category: "心血管用药",
    therapeuticClass: "ARB/钙通道阻滞剂复方",
  },
  {
    id: "extra_losartan_hydrochlorothiazide",
    nameZh: "氯沙坦钾氢氯噻嗪",
    nameEn: "losartan_hydrochlorothiazide",
    inn: "losartan_hydrochlorothiazide",
    aliasesZh: ["氯沙坦钾氢氯噻嗪", "氯沙坦钾氢氯噻嗪片", "氯沙坦氢氯噻嗪"],
    aliasesEn: ["losartan hydrochlorothiazide", "losartan/hydrochlorothiazide", "hyzaar"],
    pinyin: [],
    pinyinAbbr: "",
    category: "心血管用药",
    therapeuticClass: "ARB/利尿剂复方",
  },
  {
    id: "extra_irbesartan_hydrochlorothiazide",
    nameZh: "厄贝沙坦氢氯噻嗪",
    nameEn: "irbesartan_hydrochlorothiazide",
    inn: "irbesartan_hydrochlorothiazide",
    aliasesZh: ["厄贝沙坦氢氯噻嗪", "厄贝沙坦氢氯噻嗪片"],
    aliasesEn: ["irbesartan hydrochlorothiazide", "irbesartan/hydrochlorothiazide", "avalide"],
    pinyin: [],
    pinyinAbbr: "",
    category: "心血管用药",
    therapeuticClass: "ARB/利尿剂复方",
  },
  {
    id: "extra_telmisartan_hydrochlorothiazide",
    nameZh: "替米沙坦氢氯噻嗪",
    nameEn: "telmisartan_hydrochlorothiazide",
    inn: "telmisartan_hydrochlorothiazide",
    aliasesZh: ["替米沙坦氢氯噻嗪", "替米沙坦氢氯噻嗪片"],
    aliasesEn: ["telmisartan hydrochlorothiazide", "telmisartan/hydrochlorothiazide", "micardis hct"],
    pinyin: [],
    pinyinAbbr: "",
    category: "心血管用药",
    therapeuticClass: "ARB/利尿剂复方",
  },
  {
    id: "extra_telmisartan_amlodipine",
    nameZh: "替米沙坦氨氯地平",
    nameEn: "telmisartan_amlodipine",
    inn: "telmisartan_amlodipine",
    aliasesZh: ["替米沙坦氨氯地平", "替米沙坦氨氯地平片", "氨氯地平替米沙坦片"],
    aliasesEn: ["telmisartan amlodipine", "telmisartan/amlodipine", "twynsta"],
    pinyin: [],
    pinyinAbbr: "",
    category: "心血管用药",
    therapeuticClass: "ARB/钙通道阻滞剂复方",
  },
  {
    id: "extra_dapagliflozin_metformin",
    nameZh: "达格列净二甲双胍",
    nameEn: "dapagliflozin_metformin",
    inn: "dapagliflozin_metformin",
    aliasesZh: ["达格列净二甲双胍", "达格列净二甲双胍片", "达格列净二甲双胍缓释片"],
    aliasesEn: ["dapagliflozin metformin", "dapagliflozin/metformin", "xigduo", "xigduo xr"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "SGLT-2抑制剂/双胍复方",
  },
  {
    id: "extra_empagliflozin_metformin",
    nameZh: "恩格列净二甲双胍",
    nameEn: "empagliflozin_metformin",
    inn: "empagliflozin_metformin",
    aliasesZh: ["恩格列净二甲双胍", "恩格列净二甲双胍片", "恩格列净二甲双胍缓释片"],
    aliasesEn: ["empagliflozin metformin", "empagliflozin/metformin", "synjardy"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "SGLT-2抑制剂/双胍复方",
  },
  {
    id: "extra_canagliflozin_metformin",
    nameZh: "卡格列净二甲双胍",
    nameEn: "canagliflozin_metformin",
    inn: "canagliflozin_metformin",
    aliasesZh: ["卡格列净二甲双胍", "卡格列净二甲双胍片", "卡格列净二甲双胍缓释片"],
    aliasesEn: ["canagliflozin metformin", "canagliflozin/metformin", "invokamet"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "SGLT-2抑制剂/双胍复方",
  },
  {
    id: "extra_ertugliflozin_metformin",
    nameZh: "艾托格列净二甲双胍",
    nameEn: "ertugliflozin_metformin",
    inn: "ertugliflozin_metformin",
    aliasesZh: ["艾托格列净二甲双胍", "艾托格列净二甲双胍片"],
    aliasesEn: ["ertugliflozin metformin", "ertugliflozin/metformin", "segluromet"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "SGLT-2抑制剂/双胍复方",
  },
  {
    id: "extra_pioglitazone_metformin",
    nameZh: "吡格列酮二甲双胍",
    nameEn: "pioglitazone_metformin",
    inn: "pioglitazone_metformin",
    aliasesZh: ["吡格列酮二甲双胍", "吡格列酮二甲双胍片", "盐酸吡格列酮二甲双胍片"],
    aliasesEn: ["pioglitazone metformin", "pioglitazone/metformin", "actoplus met"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "TZD/双胍复方",
  },
  {
    id: "extra_rosiglitazone_metformin",
    nameZh: "罗格列酮二甲双胍",
    nameEn: "rosiglitazone_metformin",
    inn: "rosiglitazone_metformin",
    aliasesZh: ["罗格列酮二甲双胍", "罗格列酮二甲双胍片"],
    aliasesEn: ["rosiglitazone metformin", "rosiglitazone/metformin", "avandamet"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "TZD/双胍复方",
  },
  {
    id: "extra_glimepiride_metformin",
    nameZh: "格列美脲二甲双胍",
    nameEn: "glimepiride_metformin",
    inn: "glimepiride_metformin",
    aliasesZh: ["格列美脲二甲双胍", "格列美脲二甲双胍片"],
    aliasesEn: ["glimepiride metformin", "glimepiride/metformin"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "磺酰脲/双胍复方",
  },
  {
    id: "extra_glyburide_metformin",
    nameZh: "格列本脲二甲双胍",
    nameEn: "glyburide_metformin",
    inn: "glyburide_metformin",
    aliasesZh: ["格列本脲二甲双胍", "格列本脲二甲双胍片", "格列苯脲二甲双胍片"],
    aliasesEn: ["glyburide metformin", "glyburide/metformin", "glibenclamide metformin", "glucovance"],
    pinyin: [],
    pinyinAbbr: "",
    category: "降糖药",
    therapeuticClass: "磺酰脲/双胍复方",
  },
];

function normalizeSearchText(value: string): string {
  return normalize(value)
    .replace(/[（）()[\]{}]/g, "")
    .replace(/[＋+／/\\·,，;；:：\s_-]+/g, "");
}

function addVariant(set: Set<string>, value: string) {
  const trimmed = value.trim();
  if (trimmed) set.add(trimmed);
}

function stripDosageForm(value: string): string {
  let current = value.trim();
  let changed = true;
  while (changed) {
    changed = false;
    for (const suffix of DOSAGE_FORM_SUFFIXES) {
      if (current.endsWith(suffix) && current.length > suffix.length + 1) {
        current = current.slice(0, -suffix.length).trim();
        changed = true;
        break;
      }
    }
  }
  return current;
}

function addSaltVariants(set: Set<string>, value: string) {
  addVariant(set, value);
  for (const prefix of SALT_PREFIXES) {
    if (value.startsWith(prefix) && value.length > prefix.length + 1) {
      addVariant(set, value.slice(prefix.length));
    }
  }
  for (const suffix of SALT_SUFFIXES) {
    if (value.endsWith(suffix) && value.length > suffix.length + 1) {
      addVariant(set, value.slice(0, -suffix.length));
    }
  }
}

export function buildDrugQueryVariants(query: string): string[] {
  const variants = new Set<string>();
  const raw = query.trim();
  if (!raw) return [];

  addVariant(variants, raw);

  const bilingualMatch = raw.match(/^(.+?)\s*[（(]\s*([^)）]+)\s*[)）]\s*$/);
  if (bilingualMatch) {
    addVariant(variants, bilingualMatch[1] || "");
    addVariant(variants, bilingualMatch[2] || "");
  }

  const withoutForm = stripDosageForm(raw);
  addVariant(variants, withoutForm);

  for (const value of Array.from(variants)) {
    const stripped = stripDosageForm(value);
    addSaltVariants(variants, value);
    addSaltVariants(variants, stripped);
    if (stripped.startsWith("复方") && stripped.length > 2) {
      addSaltVariants(variants, stripped.slice(2));
    }
    addVariant(variants, value.replace(/\s+/g, " "));
    if (/[a-z]/i.test(value)) {
      addVariant(variants, value.replace(/[ _-]+/g, "_").toLowerCase());
      addVariant(variants, value.replace(/[ _-]+/g, " ").toLowerCase());
    }
  }

  return Array.from(variants).filter(Boolean);
}

function makeDrug(inn: string, nameZh: string, index: number): DrugMapping {
  const meta = inferClass(inn);
  const brandAliases = Object.entries(ALL_BRAND_TO_INN)
    .filter(([, mappedInn]) => mappedInn === inn)
    .map(([brand]) => brand);

  return {
    id: `inn_${index}_${inn.replace(/[^a-z0-9]+/gi, "_")}`,
    nameZh,
    nameEn: inn,
    inn,
    aliasesZh: [nameZh],
    aliasesEn: [inn, ...brandAliases],
    pinyin: [],
    pinyinAbbr: "",
    category: meta.category,
    therapeuticClass: meta.therapeuticClass,
  };
}

export const drugMappings: DrugMapping[] = [
  ...Object.entries(ALL_INN_TO_CN).map(([inn, cn], index) => makeDrug(inn, cn, index)),
  ...EXTRA_DRUG_MAPPINGS,
].filter((drug, index, all) =>
  all.findIndex(item => normalize(item.nameEn) === normalize(drug.nameEn)) === index
);

function bestTermScore(term: string, queryVariants: string[]): number {
  const normalizedTerm = normalizeSearchText(term);
  if (!normalizedTerm) return 0;

  return queryVariants.reduce((best, variant) => {
    const normalizedVariant = normalizeSearchText(variant);
    if (!normalizedVariant) return best;
    if (normalizedTerm === normalizedVariant) return Math.max(best, 100);
    if (normalizedTerm.startsWith(normalizedVariant)) return Math.max(best, 82);
    if (normalizedVariant.startsWith(normalizedTerm) && normalizedTerm.length >= 3) return Math.max(best, 78);
    if (normalizedTerm.includes(normalizedVariant)) return Math.max(best, 64);
    if (normalizedVariant.includes(normalizedTerm) && normalizedTerm.length >= 3) return Math.max(best, 58);
    return best;
  }, 0);
}

function directLookupInn(queryVariants: string[]): string {
  for (const variant of queryVariants) {
    const direct = ALL_CN_TO_INN[variant] || ALL_BRAND_TO_INN[normalize(variant)];
    if (direct) return direct;
  }

  for (const drug of EXTRA_DRUG_MAPPINGS) {
    const terms = [drug.nameZh, drug.nameEn, drug.inn || "", ...drug.aliasesZh, ...drug.aliasesEn];
    if (terms.some(term => bestTermScore(term, queryVariants) >= 100)) {
      return drug.nameEn;
    }
  }

  return "";
}

export function searchDrugs(query: string, limit = 10): DrugMapping[] {
  const queryVariants = buildDrugQueryVariants(query);
  if (queryVariants.length === 0) return drugMappings.slice(0, limit);

  const directInn = directLookupInn(queryVariants);
  const normalizedQuery = directInn ? normalize(directInn) : normalize(queryVariants[0]);

  const scored = drugMappings
    .map(drug => {
      const inn = normalize(drug.nameEn);
      const terms = [
        drug.nameZh,
        drug.nameEn,
        drug.inn || "",
        ...drug.aliasesZh,
        ...drug.aliasesEn,
      ];

      const termScore = Math.max(...terms.map(term => bestTermScore(term, queryVariants)));
      let score = termScore;
      if (directInn && inn === normalizedQuery) score = Math.max(score, 105);
      else if (inn === normalizedQuery) score = Math.max(score, 100);

      return { drug, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score || a.drug.nameEn.localeCompare(b.drug.nameEn));

  return scored.slice(0, limit).map(item => item.drug);
}

export function formatBilingualDisplay(drug: DrugMapping): string {
  return `${drug.nameZh} (${drug.nameEn})`;
}
